'use client';

import { stringIsUuidv4, titleCase } from '@wolfejs/core/utils/string';
import { ChevronRight } from '@wolfejs/icons/IconMap';
import type { ReactNode } from 'react';
import React, { useCallback, useMemo, useRef } from 'react';
import type { UIComponentProps, UIThemableComponent } from '../types/UI';
import { cn } from '../utils/classnames';

export type BreadcrumbItemType = {
  label: string;
  url?: string;
};

export type BreadcrumbsProps = UIThemableComponent<UIComponentProps<HTMLUListElement>, HTMLUListElement> & {
  ref?: React.ForwardedRef<HTMLUListElement>;

  items?: BreadcrumbItemType[];
  pathname?: string;
  separator?: string | ReactNode;
  ignoreSlug?: boolean;
  renderItem?: (item: BreadcrumbItemType, index: number) => React.ReactNode;
};

function obfuscateUUID(value: string) {
  return `${value?.slice(0, 6)}...${value?.slice(value.length - 7, value.length - 1)}`;
}

export function Breadcrumbs({
  cname = 'ui-Breadcrumbs',
  items,
  pathname = '',
  separator = <ChevronRight className="text-default-300" />,
  renderItem,
  className,
  classNames,
  ignoreSlug = false,
  ...otherProps
}: BreadcrumbsProps): React.ReactElement {
  const navItems: BreadcrumbItemType[] = items || [];
  const ref = useRef(navItems);

  const generateUrl = useCallback((pathsArray: string[], name: string): string => {
    const lastIndex = pathsArray.indexOf(name) + 1;
    const changedArray = pathsArray.map(item => (stringIsUuidv4(item) ? item + '/details' : item));
    return changedArray.slice(0, lastIndex).join('/');
  }, []);

  const generateName = useCallback((name: string): string => {
    if (name.includes('_')) {
      const nameArray = name.split('_');
      return stringIsUuidv4(nameArray[1]) ? `${obfuscateUUID(nameArray[1])}...` : titleCase(`${nameArray[0]} Details`);
    } else {
      return stringIsUuidv4(name) ? `${obfuscateUUID(name)}...` : titleCase(name);
    }
  }, []);

  const generateBreadCrumbsItem = useCallback(
    (pathname: string): BreadcrumbItemType[] => {
      const items: BreadcrumbItemType[] = [];
      const pathArray = pathname.split('/');
      (stringIsUuidv4(pathArray[1]) || ignoreSlug) && pathArray.splice(1, 1);
      pathArray.map(name => {
        if (name.length > 0) {
          items.push({
            label: generateName(name),
            url: generateUrl(pathname.split('/'), name),
          });
        }
      });
      return items;
    },
    [generateName, generateUrl, ignoreSlug]
  );

  useMemo(() => {
    if (ref.current.length < 1 && pathname) {
      ref.current = generateBreadCrumbsItem(pathname);
    }
  }, [pathname, generateBreadCrumbsItem]);

  return (
    <ul className={cn(cname, 'flex gap-2', className)} {...otherProps}>
      {ref.current?.map((item, index) => {
        const isLastItem = !!(index >= ref.current.length - 1);
        return (
          <li key={index} className={cn(cname, 'flex gap-2', classNames?.item)}>
            {item.url !== undefined && !isLastItem ? (
              renderItem ? (
                renderItem(item, index)
              ) : (
                <a href={item.url} className={cn('text-foreground', classNames?.link)}>
                  {item.label}
                </a>
              )
            ) : (
              <div className="text-default-400">{item.label}</div>
            )}
            {!isLastItem && <div className="flex items-center">{separator}</div>}
          </li>
        );
      })}
    </ul>
  );
}
